import * as THREE from 'three';
import {store} from 'store/customer';
import {CabinetMesh, RoomType} from 'components/customer/RoomPlanner/types';
import {handleSnapping} from 'components/customer/RoomPlanner/helpers/cabinetSnapping';
import {applySnapState} from 'components/customer/RoomPlanner/helpers/snapping/state';
import {isSnapCandidate} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import {
    checkCabinetBodyOverlap,
    checkCabinetOverlap,
} from 'components/customer/RoomPlanner/helpers/overlap';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    getGroupWidth,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    getSitOnFloorY,
    getWallTopY,
} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {handleFillerSwitching} from 'components/customer/RoomPlanner/helpers/fillerSwitching';
import {syncOverlaysOnCabinetMove} from 'components/customer/RoomPlanner/helpers/syncOverlayOnCabinetMove';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';

// Nudge distance constants (in mm)

// Scene units = mm * 0.2 (so 0.5mm => 0.1 units — matches "move by .1" in scaled space)
export const NUDGE_MICRO = 0.1; // Ctrl+Shift + Arrow/Page — 0.1 scene units per keypress
export const NUDGE_FINE = 1; // Shift + Arrow
export const NUDGE_STANDARD = 10; // Plain Arrow
export const NUDGE_COARSE = 100; // Ctrl/Cmd + Arrow

// Continuous held-key movement. OS key-repeat (long initial delay, then
// bursty repeats) makes held arrows feel laggy/robotic, so repeat events
// are ignored and a requestAnimationFrame loop drives movement instead.
const HELD_MOVE_START_DELAY_MS = 300; // hold time before continuous movement starts (quick taps = one step)
const HELD_MOVE_STEP_MS = 40; // cadence of continuous steps (~25/s)
const HELD_MOVE_UI_SYNC_MS = 120; // throttle for arrow/measurement overlay rebuilds
const HELD_MOVE_MAX_CATCHUP_STEPS = 3; // cap catch-up after slow frames

interface HeldMoveModifiers {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
}

const getArrowNudgeAmount = (modifiers: HeldMoveModifiers): number => {
    if ((modifiers.ctrlKey || modifiers.metaKey) && modifiers.shiftKey) {
        return NUDGE_MICRO;
    }
    if (modifiers.ctrlKey || modifiers.metaKey) {
        return NUDGE_COARSE;
    }
    if (modifiers.shiftKey) {
        return NUDGE_FINE;
    }
    return NUDGE_STANDARD;
};

const getPageNudgeAmount = (modifiers: HeldMoveModifiers): number => {
    if (modifiers.shiftKey) {
        return NUDGE_FINE;
    }
    if (modifiers.ctrlKey || modifiers.metaKey) {
        return NUDGE_COARSE;
    }
    return NUDGE_STANDARD;
};

interface HeldMovementState {
    key: string;
    modifiers: HeldMoveModifiers;
    config: KeyboardControlsConfig;
    rafId: number;
    lastStepTime: number;
    lastUiSyncTime: number;
    stopListeners: () => void;
}

let heldMovement: HeldMovementState | null = null;

/**
 * Stops the continuous held-key movement loop and does a final overlay sync.
 * @export
 */
export const stopHeldMovement = (): void => {
    if (!heldMovement) return;

    const {config, rafId, stopListeners} = heldMovement;
    cancelAnimationFrame(rafId);
    stopListeners();
    heldMovement = null;

    // Final sync so fillers and arrows/measurements land at the end position
    if (config.selectedCabinetGroup) {
        const wallMesh = config.scene.children.find(
            (ch) =>
                ch.name === ObjectNameMap.rectangularWall ||
                ch.name === ObjectNameMap.lShapeWall
        );
        if (wallMesh) {
            handleFillerSwitching(config.products, wallMesh.children);
        }
        updateArrowPositions(config.selectedCabinetGroup, config.scene);
        productEvents.revalidateOverlaps();
    }
};

/**
 * Refreshes modifier keys and config for the active held-movement loop
 * (called from OS key-repeat events, which are otherwise ignored).
 * @param {KeyboardEvent} event - The repeat keydown event
 * @param {KeyboardControlsConfig} config - Latest configuration object
 */
const updateHeldMovement = (
    event: KeyboardEvent,
    config: KeyboardControlsConfig
): void => {
    if (!heldMovement || heldMovement.key !== event.key) return;
    heldMovement.modifiers = {
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
    };
    heldMovement.config = config;
};

/**
 * Starts a requestAnimationFrame loop that moves the cabinet at a steady
 * cadence while a movement key is held. Runs until keyup/blur/Escape.
 * @param {KeyboardEvent} event - The initial (non-repeat) keydown event
 * @param {KeyboardControlsConfig} config - Configuration object
 */
const startHeldMovement = (
    event: KeyboardEvent,
    config: KeyboardControlsConfig
): void => {
    // Restart cleanly if another movement key takes over
    stopHeldMovement();

    const key = event.key;
    const isPageKey = key === 'PageUp' || key === 'PageDown';

    const onKeyUp = (keyUpEvent: KeyboardEvent) => {
        if (keyUpEvent.key === key || keyUpEvent.key === 'Escape') {
            stopHeldMovement();
        }
    };
    const onBlur = () => stopHeldMovement();

    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onBlur);

    // Continuous movement only begins after a short hold, so a quick tap
    // produces exactly one step (from the initial keydown) and never two.
    const firstStepTime = performance.now() + HELD_MOVE_START_DELAY_MS;

    const state: HeldMovementState = {
        key,
        modifiers: {
            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
        },
        config,
        rafId: 0,
        lastStepTime: firstStepTime,
        lastUiSyncTime: firstStepTime,
        stopListeners: () => {
            window.removeEventListener('keyup', onKeyUp, true);
            window.removeEventListener('blur', onBlur);
        },
    };

    const tick = (now: number) => {
        if (heldMovement !== state) return;

        if (!state.config.selectedCabinetGroup) {
            stopHeldMovement();
            return;
        }

        const elapsed = now - state.lastStepTime;
        let steps = Math.floor(elapsed / HELD_MOVE_STEP_MS);

        if (steps > 0) {
            state.lastStepTime += steps * HELD_MOVE_STEP_MS;
            steps = Math.min(steps, HELD_MOVE_MAX_CATCHUP_STEPS);

            // Overlay rebuilds (arrows/measurements) are the expensive part;
            // throttle them and let the cabinet itself move every step.
            const syncUi = now - state.lastUiSyncTime >= HELD_MOVE_UI_SYNC_MS;
            if (syncUi) {
                state.lastUiSyncTime = now;
            }

            for (let i = 0; i < steps; i++) {
                const isLastStep = i === steps - 1;
                const moved = isPageKey
                    ? performPageStep(
                          key,
                          state.modifiers,
                          state.config,
                          true,
                          syncUi && isLastStep
                      )
                    : performArrowStep(
                          key,
                          state.modifiers,
                          state.config,
                          true,
                          syncUi && isLastStep
                      );

                // Blocked (overlap/boundary) — no point stepping further
                // this frame, but keep the loop alive in case it frees up.
                if (!moved) break;
            }
        }

        state.rafId = requestAnimationFrame(tick);
    };

    heldMovement = state;
    state.rafId = requestAnimationFrame(tick);
};

/**
 * Deactivates movement mode for a cabinet and hides all associated arrows and measurements
 * This includes:
 * - Turning off movement mode flag
 * - Resetting arrow hover states
 * - Calling setActiveState and setDistanceMeasurementsActive callbacks
 * - Hiding movement arrows (cabinetMovementArrows)
 * - Hiding distance wall measurements
 *
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh to deactivate movement mode for
 * @param {THREE.Scene} scene - The scene containing the arrows and measurements
 * @export
 */
export const deactivateMovementMode = (
    cabinetGroup: CabinetMesh,
    scene: THREE.Scene
): void => {
    if (!cabinetGroup) return;

    // Turn off movement mode
    if (cabinetGroup.userData.isMovementMode) {
        cabinetGroup.userData.isMovementMode = false;

        // Reset hover states for arrows in the arrow group
        const userDataWithArrowGroup = cabinetGroup.userData as {
            arrowGroup?: THREE.Group;
        };

        if (userDataWithArrowGroup.arrowGroup) {
            const arrowGroup = userDataWithArrowGroup.arrowGroup;

            if (arrowGroup.children) {
                arrowGroup.children.forEach((arrow: THREE.Object3D) => {
                    if (
                        arrow.userData &&
                        arrow.userData.isArrow &&
                        arrow.userData.isHovered
                    ) {
                        if (typeof arrow.userData.onHoverEnd === 'function') {
                            (
                                arrow.userData as {
                                    onHoverEnd: () => void;
                                }
                            ).onHoverEnd();
                        }
                        arrow.userData.isHovered = false;
                    }
                });
            }
        }

        // Call userData methods to update UI state
        const userData = cabinetGroup.userData as {
            setActiveState?: (isActive: boolean) => void;
            setDistanceMeasurementsActive?: (isActive: boolean) => void;
        };

        if (typeof userData?.setActiveState === 'function') {
            userData.setActiveState(false);
        }

        if (typeof userData?.setDistanceMeasurementsActive === 'function') {
            userData.setDistanceMeasurementsActive(false);
        }
    }

    // Hide movement arrows for this cabinet
    scene.children.forEach((child: THREE.Object3D) => {
        if (
            child.name === 'cabinetMovementArrows' &&
            child.userData?.id === cabinetGroup.userData.id
        ) {
            child.visible = false;
            child.traverse((arrow) => {
                arrow.visible = false;
            });
        }
    });

    // Hide distance wall measurements
    scene.children.forEach((child: THREE.Object3D) => {
        if (child.name === 'distanceWallMeasurements') {
            child.visible = false;
            child.traverse((measurement) => {
                measurement.visible = false;
            });
        }
    });
};

interface KeyboardControlsConfig {
    selectedCabinetGroup: CabinetMesh | null;
    debouncedSave: (mesh: CabinetMesh) => void;
    setSelectedCabinetGroup: (mesh: CabinetMesh | null) => void;
    products: CabinetMesh[];
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    roomType: RoomType;
    containerRef: React.RefObject<HTMLDivElement>;
    hiddenWall: string | null;
    isRoomPreview?: boolean;
}

const isFloorLikeProduct = (mesh: CabinetMesh): boolean =>
    Boolean(
        mesh.userData?.isPlaceholder ||
        mesh.userData?.isFloorProduct ||
        mesh.userData?.noBottom
    );

/**
 * Cabinets + placeholders, same list drag collision uses.
 * @param {CabinetMesh[]} fallback Layout meshes when the live list is empty
 * @return {CabinetMesh[]}
 */
const getCollisionProducts = (fallback: CabinetMesh[]): CabinetMesh[] => {
    const meshes = productEvents.getLayoutMeshes();
    if (meshes.length === 0) return fallback;
    if (fallback.length === 0) return meshes;

    const seen = new Set<string>();
    const merged: CabinetMesh[] = [];
    for (const mesh of meshes.concat(fallback)) {
        if (seen.has(mesh.uuid)) continue;
        seen.add(mesh.uuid);
        merged.push(mesh);
    }
    return merged;
};

const doesMoveOverlap = (
    overlapping: CabinetMesh[] | null,
    moving: CabinetMesh
): boolean => {
    const bothFloorProducts =
        Boolean(overlapping?.some(isFloorLikeProduct)) &&
        isFloorLikeProduct(moving);

    return (
        Boolean(overlapping) &&
        (bothFloorProducts || !moving.userData.ignoreOverlap)
    );
};

/**
 * Handles Escape key - Deselects current object or exits placement mode
 * @param {KeyboardEvent} event - The keyboard event
 * @param {KeyboardControlsConfig} config - Configuration object
 * @return {boolean} True if event was handled
 */
const handleEscapeKey = (
    event: KeyboardEvent,
    {
        selectedCabinetGroup,
        setSelectedCabinetGroup,
        scene,
    }: KeyboardControlsConfig
) => {
    if (event.key === 'Escape') {
        event.preventDefault();
        if (selectedCabinetGroup) {
            // Reset color indicators and visual state before deselecting
            selectedCabinetGroup.traverse((child) => {
                const userData = child.userData as {
                    resetSegmentColor?: (reset: boolean) => void;
                };
                if (typeof userData?.resetSegmentColor === 'function') {
                    userData.resetSegmentColor(false);
                }
            });

            // Deactivate movement mode and hide arrows/measurements
            deactivateMovementMode(selectedCabinetGroup, scene);

            // Clear ProductEvents' own selection too, otherwise the overlap
            // revalidation that follows deselection re-highlights it.
            productEvents.deselectCabinet();

            // Deselect the current object
            setSelectedCabinetGroup(null);
        }
        return true;
    }
    return false;
};

/**
 * Handles Space key - Rotates object clockwise or counter-clockwise
 * @param {KeyboardEvent} event - The keyboard event
 * @param {KeyboardControlsConfig} config - Configuration object
 * @return {boolean} True if event was handled
 */
const handleSpaceKey = (
    event: KeyboardEvent,
    {selectedCabinetGroup, debouncedSave, scene}: KeyboardControlsConfig
) => {
    if (event.key === ' ' && selectedCabinetGroup) {
        event.preventDefault();
        const userData = selectedCabinetGroup.userData as {
            rotateRight?: () => void;
            rotateLeft?: () => void;
        };

        // Shift + Space: Rotate anti-clockwise (left)
        if (event.shiftKey) {
            if (typeof userData?.rotateLeft === 'function') {
                userData.rotateLeft();
            } else {
                // Fallback: rotate anti-clockwise manually
                rotateObject(selectedCabinetGroup, -90);
            }
        } else {
            // Plain Space: Rotate clockwise (right)
            if (typeof userData?.rotateRight === 'function') {
                userData.rotateRight();
            } else {
                // Fallback: rotate clockwise manually
                rotateObject(selectedCabinetGroup, 90);
            }
        }
        debouncedSave(selectedCabinetGroup);
        // Keep movement overlays in sync (same as arrow keys); rotating toggles Redux
        // `rotating` which re-runs layout, but this applies visibility immediately.
        updateArrowPositions(selectedCabinetGroup, scene);
        return true;
    }
    return false;
};

/**
 * Performs a single vertical (Page Up/Down) movement step.
 * @param {string} key - 'PageUp' or 'PageDown'
 * @param {HeldMoveModifiers} modifiers - Active modifier keys
 * @param {KeyboardControlsConfig} config - Configuration object
 * @param {boolean} isHeldRepeat - True for continuous held-key steps
 * @param {boolean} syncUi - Whether to rebuild overlays this step
 * @return {boolean} True if movement was successful
 */
const performPageStep = (
    key: string,
    modifiers: HeldMoveModifiers,
    config: KeyboardControlsConfig,
    isHeldRepeat: boolean,
    syncUi: boolean
): boolean => {
    const {selectedCabinetGroup, debouncedSave} = config;
    if (!selectedCabinetGroup) return false;

    const adjustedStep = getPageNudgeAmount(modifiers) * 0.2;
    const movementVector = new THREE.Vector3(
        0,
        key === 'PageUp' ? adjustedStep : -adjustedStep,
        0
    );
    const newPosition = selectedCabinetGroup.position
        .clone()
        .add(movementVector);

    const moveSuccess = updateCabinetPosition(
        selectedCabinetGroup,
        newPosition,
        config,
        {altKey: false, repeat: isHeldRepeat},
        true, // isVerticalMovement - don't snap Y-axis movements
        syncUi
    );

    if (moveSuccess) {
        debouncedSave(selectedCabinetGroup);
    }
    return moveSuccess;
};

/**
 * Handles Page Up and Page Down keys - Vertical movement
 * Modifiers:
 * - Shift: Fine movement (1mm)
 * - Ctrl/Cmd: Coarse movement (100mm)
 * Note: Vertical movement does not use snapping regardless of Alt key
 * Performance: Held keys are driven by a rAF loop; OS repeats are ignored
 * @param {KeyboardEvent} event - The keyboard event
 * @param {KeyboardControlsConfig} config - Configuration object
 * @return {boolean} True if event was handled
 */
const handlePageKeys = (
    event: KeyboardEvent,
    config: KeyboardControlsConfig
) => {
    const {selectedCabinetGroup} = config;

    // Check if it's a Page key
    if (['PageUp', 'PageDown'].includes(event.key)) {
        // Prevent default browser behavior (text selection, scrolling) early
        if (selectedCabinetGroup) {
            event.preventDefault();
            event.stopPropagation(); // Optional, but safer
        }

        // Only proceed if there's a selected cabinet
        if (!selectedCabinetGroup) return false;

        // OS key-repeat events only refresh modifier state; the held-move
        // loop performs the actual movement at a steady cadence.
        if (event.repeat) {
            updateHeldMovement(event, config);
            return true;
        }

        performPageStep(event.key, event, config, false, true);
        startHeldMovement(event, config);
        return true;
    }
    return false;
};

/**
 * Transforms arrow key direction based on which wall is hidden (camera view direction)
 * This makes arrow keys work relative to the camera's perspective
 *
 * Wall orientations:
 * - backWall hidden = camera looking at back wall (from front/+Z) - normal controls
 * - frontWall hidden = camera looking at front wall (from back/-Z) - inverted controls
 * - leftWall hidden = camera looking at left wall (from right/+X) - rotated 90° counter-clockwise
 * - rightWall hidden = camera looking at right wall (from left/-X) - rotated 90° clockwise
 *
 * @param {string} arrowKey - The arrow key pressed (ArrowLeft, ArrowRight, ArrowUp, ArrowDown)
 * @param {string | null} hiddenWall - The hidden wall (from camera position)
 * @return {THREE.Vector3} The transformed movement vector (unit vector, scale separately)
 */
export const getArrowMovementVector = (
    arrowKey: string,
    hiddenWall: string | null
): THREE.Vector3 => {
    const movementVector = new THREE.Vector3();

    // Extract primary wall from combined wall strings (e.g., "rightWall,frontWall" -> "rightWall")
    const primaryWall = hiddenWall?.split(',')[0] || 'backWall';

    // Define movement transformations based on camera view (which wall is hidden)
    switch (primaryWall) {
        case 'backWall':
            // Camera at +Z looking at back wall (normal view)
            // ArrowLeft = move left (-X), ArrowRight = move right (+X)
            // ArrowUp = move away (-Z), ArrowDown = move closer (+Z)
            switch (arrowKey) {
                case 'ArrowLeft':
                    movementVector.set(1, 0, 0);
                    break;
                case 'ArrowRight':
                    movementVector.set(-1, 0, 0);
                    break;
                case 'ArrowUp':
                    movementVector.set(0, 0, 1);
                    break;
                case 'ArrowDown':
                    movementVector.set(0, 0, -1);
                    break;
            }
            break;
        case 'frontWall':
            // Camera at -Z looking at front wall (opposite view)
            // ArrowLeft = move right (+X), ArrowRight = move left (-X)
            // ArrowUp = move closer (+Z), ArrowDown = move away (-Z)
            switch (arrowKey) {
                case 'ArrowLeft':
                    movementVector.set(-1, 0, 0);
                    break;
                case 'ArrowRight':
                    movementVector.set(1, 0, 0);
                    break;
                case 'ArrowUp':
                    movementVector.set(0, 0, -1);
                    break;
                case 'ArrowDown':
                    movementVector.set(0, 0, 1);
                    break;
            }
            break;
        case 'rightWall':
            // Camera at -X looking at right wall (rotated 90° clockwise from back view)
            // ArrowLeft = move away (-Z), ArrowRight = move closer (+Z)
            // ArrowUp = move left (-X), ArrowDown = move right (+X)
            switch (arrowKey) {
                case 'ArrowLeft':
                    movementVector.set(0, 0, 1);
                    break;
                case 'ArrowRight':
                    movementVector.set(0, 0, -1);
                    break;
                case 'ArrowUp':
                    movementVector.set(-1, 0, 0);
                    break;
                case 'ArrowDown':
                    movementVector.set(1, 0, 0);
                    break;
            }
            break;
        case 'leftWall':
            // Camera at +X looking at left wall (rotated 90° counter-clockwise from back view)
            // ArrowLeft = move closer (+Z), ArrowRight = move away (-Z)
            // ArrowUp = move right (+X), ArrowDown = move left (-X)
            switch (arrowKey) {
                case 'ArrowLeft':
                    movementVector.set(0, 0, -1);
                    break;
                case 'ArrowRight':
                    movementVector.set(0, 0, 1);
                    break;
                case 'ArrowUp':
                    movementVector.set(1, 0, 0);
                    break;
                case 'ArrowDown':
                    movementVector.set(-1, 0, 0);
                    break;
            }
            break;
        default:
            // Fallback to normal (backWall) behavior
            switch (arrowKey) {
                case 'ArrowLeft':
                    movementVector.set(-1, 0, 0);
                    break;
                case 'ArrowRight':
                    movementVector.set(1, 0, 0);
                    break;
                case 'ArrowUp':
                    movementVector.set(0, 0, -1);
                    break;
                case 'ArrowDown':
                    movementVector.set(0, 0, 1);
                    break;
            }
    }

    return movementVector;
};

/**
 * Performs a single horizontal (arrow key) movement step.
 * @param {string} key - The arrow key
 * @param {HeldMoveModifiers} modifiers - Active modifier keys
 * @param {KeyboardControlsConfig} config - Configuration object
 * @param {boolean} isHeldRepeat - True for continuous held-key steps
 * @param {boolean} syncUi - Whether to rebuild overlays this step
 * @param {boolean} altKey - Alt key state (enables snapping on first press)
 * @return {boolean} True if movement was successful
 */
const performArrowStep = (
    key: string,
    modifiers: HeldMoveModifiers,
    config: KeyboardControlsConfig,
    isHeldRepeat: boolean,
    syncUi: boolean,
    altKey = false
): boolean => {
    const {selectedCabinetGroup, debouncedSave} = config;
    if (!selectedCabinetGroup) return false;

    const adjustedStep = getArrowNudgeAmount(modifiers) * 0.2;

    // Same camera-relative movement for base and upper cabinets
    const unitVector = getArrowMovementVector(key, config.hiddenWall);
    const movementVector = unitVector.multiplyScalar(adjustedStep);
    const newPosition = selectedCabinetGroup.position
        .clone()
        .add(movementVector);

    const moveSuccess = updateCabinetPosition(
        selectedCabinetGroup,
        newPosition,
        config,
        {altKey, repeat: isHeldRepeat},
        false, // isVerticalMovement - allow snapping for arrow key movements
        syncUi
    );

    if (moveSuccess) {
        debouncedSave(selectedCabinetGroup);
    }
    return moveSuccess;
};

/**
 * Handles Arrow keys - Horizontal and depth movement
 * Modifiers:
 * - Shift: Fine movement (1mm)
 * - Ctrl/Cmd: Coarse movement (100mm)
 * - Alt: Enable snapping to nearby cabinets (disabled by default)
 * Note: Snapping only works on first key press when Alt is held, disabled while holding the key down
 * Note: Arrow directions are automatically adjusted based on camera view (which wall is hidden)
 * Performance: Held keys are driven by a rAF loop; OS repeats are ignored
 * @param {KeyboardEvent} event - The keyboard event
 * @param {KeyboardControlsConfig} config - Configuration object
 * @return {boolean} True if event was handled
 */
const handleArrowKeys = (
    event: KeyboardEvent,
    config: KeyboardControlsConfig
) => {
    const {selectedCabinetGroup} = config;

    // Check if it's an arrow key
    if (
        ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
    ) {
        // Only handle if there's a selected cabinet
        if (!selectedCabinetGroup) {
            return false;
        }

        // Prevent default browser behavior for all arrow keys when cabinet is selected
        // This includes scrolling (ArrowUp/Down) and browser navigation (Alt+Arrow)
        event.preventDefault();

        // OS key-repeat events only refresh modifier state; the held-move
        // loop performs the actual movement at a steady cadence.
        if (event.repeat) {
            updateHeldMovement(event, config);
            return true;
        }

        performArrowStep(event.key, event, config, false, true, event.altKey);
        startHeldMovement(event, config);
        return true;
    }
    return false;
};

/**
 * Checks if the 3D canvas has focus (not a text input, dropdown, or number field)
 * @param {HTMLElement | null} target - The event target element
 * @param {React.RefObject<HTMLDivElement>} containerRef - Reference to the canvas container
 * @return {boolean} True if canvas can receive keyboard input
 */
const canAcceptKeyboardInput = (
    target: HTMLElement | null,
    containerRef: React.RefObject<HTMLDivElement>
): boolean => {
    if (!target) return false;

    // Check if user is typing in an input field, dropdown, or number field
    const isInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
        target.tagName
    );

    if (isInputField) {
        return false;
    }

    // Check if the target is within the canvas container or is the canvas itself
    const container = containerRef.current;
    if (!container) return false;

    // Allow if the canvas container or its children have focus
    return container.contains(target) || target === document.body;
};

/**
 * Creates a keyboard event handler for room planner controls
 * Handles: Esc (deselect), Delete/Backspace, Space (rotate), Shift+Space (rotate counter-clockwise),
 * Arrow keys (horizontal nudge), Page Up/Down (vertical nudge)
 *
 * Movement Modifiers:
 * - Plain Arrow/PageUp/PageDown: Standard movement (10mm) WITHOUT snapping
 * - Shift + Arrow/PageUp/PageDown: Fine movement (1mm)
 * - Ctrl/Cmd + Arrow/PageUp/PageDown: Coarse movement (100mm)
 * - Alt + Arrow: Standard movement WITH snapping enabled (only on first key press, disabled while holding)
 *
 * @param {KeyboardControlsConfig} config - Configuration object with selected cabinet, delete handler, and save function
 * @return {Function} Keyboard event handler function
 */
export const createKeyboardHandler = (config: KeyboardControlsConfig) => {
    return (event: KeyboardEvent) => {
        // Check if the canvas can accept keyboard input
        if (
            !canAcceptKeyboardInput(
                event.target as HTMLElement,
                config.containerRef
            )
        ) {
            return;
        }

        const showCabinets = store.getState().roomPlanner.showCabinets;
        if (!showCabinets) return;

        // Handle each key event in order of priority
        if (handleEscapeKey(event, config)) return;

        if (!config.isRoomPreview) {
            if (handleSpaceKey(event, config)) return;
            if (handlePageKeys(event, config)) return;
            if (handleArrowKeys(event, config)) return;
        }
    };
};

/**
 * Wall height in scene units (mm × 0.2), same basis as cabinet Y clamping.
 * @return {number} Wall height in scene units
 */
const getWallHeightScene = (): number => {
    const state = store.getState();
    const roomType = state.roomPlanner.roomType;
    if (roomType === 'RECTANGULAR') {
        const rectDim = state.roomPlanner.rectangularDimension;
        return (rectDim.height || 2400) * 0.2;
    }
    const lShapeDim = state.roomPlanner.lShapedDimension;
    return (lShapeDim.height || 2400) * 0.2;
};

/**
 * Clamp a placeholder so its mesh stays between the floor seating plane and
 * the wall top (the same raised ceiling cabinets use). GLB pivots are often
 * not at the mesh bottom, so this uses the live AABB rather than
 * `cabinet_height * scale.x`.
 * @param {CabinetMesh} cabinetGroup - The placeholder mesh
 * @param {THREE.Vector3} targetPosition - Requested position
 * @param {number} wallHeight - Wall height in scene units
 * @return {THREE.Vector3} Position with Y clamped
 */
const applyPlaceholderVerticalClamping = (
    cabinetGroup: CabinetMesh,
    targetPosition: THREE.Vector3,
    wallHeight: number
): THREE.Vector3 => {
    const floorY = getSitOnFloorY(wallHeight);
    const ceilingY = getWallTopY(wallHeight);
    const box = computeWorldBoundsExcludingAnimatedDoorParts(cabinetGroup);

    if (
        box.isEmpty() ||
        !Number.isFinite(box.min.y) ||
        !Number.isFinite(box.max.y)
    ) {
        return targetPosition.clone();
    }

    const bottomOffset = box.min.y - cabinetGroup.position.y;
    const topOffset = box.max.y - cabinetGroup.position.y;
    const minY = floorY - bottomOffset;
    const maxY = ceilingY - topOffset;
    const clampedY =
        minY > maxY ? minY : Math.max(minY, Math.min(targetPosition.y, maxY));

    return new THREE.Vector3(targetPosition.x, clampedY, targetPosition.z);
};

/**
 * Applies vertical (Y-axis) clamping to prevent cabinets from going through floor or ceiling
 * Matches the logic from moveUp/moveDown in renderCabinetGroup.ts
 * Uses the same boundary calculations to ensure consistency
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh
 * @param {THREE.Vector3} position - The position to clamp
 * @return {THREE.Vector3} The clamped position
 */

export const applyVerticalClamping = (
    cabinetGroup: CabinetMesh,
    targetPosition: THREE.Vector3
): THREE.Vector3 => {
    const wallHeight = getWallHeightScene();

    if (cabinetGroup.userData?.isPlaceholder) {
        return applyPlaceholderVerticalClamping(
            cabinetGroup,
            targetPosition,
            wallHeight
        );
    }
    const isLShapePanel = cabinetGroup.userData.isLShapePanel;
    const isManualPanel = cabinetGroup.userData.isManualPanel;
    const isPanelProduct = isLShapePanel || isManualPanel;
    // Get cabinet dimensions using getGroupWidth (same as moveUp/moveDown)
    const {height: heightGroup} = getGroupWidth(cabinetGroup);
    const height = isPanelProduct
        ? heightGroup
        : Number(cabinetGroup.userData.values.cabinet_height);

    const scale = cabinetGroup.scale.x; // Get the current scale from the cabinet

    // Calculate upper and lower limits matching renderCabinetGroup.ts exactly
    // Upper limit from moveUp function (line 523)
    const upperLimit =
        wallHeight / 2 - height * scale + 51 - (isPanelProduct ? 3.5 : 0);

    // Lower limit from moveDown function (line 552)
    const lowerLimit = -(wallHeight / 2) + 51;

    const clampedY = Math.max(
        lowerLimit,
        Math.min(targetPosition.y, upperLimit)
    );

    return new THREE.Vector3(targetPosition.x, clampedY, targetPosition.z);
};
/**
 * Validates and updates the cabinet position with collision detection, snapping, and boundary checking
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh to update
 * @param {THREE.Vector3} newPosition - The new position vector
 * @param {KeyboardControlsConfig} config - Configuration object
 * @param {object} event - Alt/repeat key state (repeat = continuous held-key step)
 * @param {boolean} isVerticalMovement - Whether this is a vertical-only movement (skip snapping for Y-axis)
 * @param {boolean} syncUi - Whether to rebuild overlays (arrows/measurements) after the move
 * @return {boolean} True if movement was successful, false if blocked
 */
const updateCabinetPosition = (
    cabinetGroup: CabinetMesh,
    newPosition: THREE.Vector3,
    config: KeyboardControlsConfig,
    event?: {altKey?: boolean; repeat?: boolean},
    isVerticalMovement = false,
    syncUi = true
): boolean => {
    const {scene, camera, roomType} = config;
    const products = getCollisionProducts(config.products);
    const previousPosition = cabinetGroup.position.clone();

    // Apply vertical clamping first (for Y-axis movements)
    let clampedNewPosition = newPosition.clone();
    if (isVerticalMovement) {
        clampedNewPosition = applyVerticalClamping(cabinetGroup, newPosition);
        // If the Y position didn't change after clamping, the movement is blocked
        if (Math.abs(clampedNewPosition.y - previousPosition.y) < 0.1) {
            return false; // Already at boundary, can't move further
        }
    }

    const isKickerLengthProduct = cabinetGroup.userData.isKickerLengthProduct;

    // Held-key moves recheck overlap when the room contains a placeholder,
    // so a held arrow cannot pass through one. The inset itself stays -2.
    // checkCabinetBodyOverlap zeros it for pairs that include a placeholder;
    // cabinet pairs keep this inset.
    const involvesPlaceholder =
        Boolean(cabinetGroup.userData?.isPlaceholder) ||
        products.some((mesh) => mesh.userData?.isPlaceholder);
    const escapeBuffer = -2;

    // Skip overlap check for non-manual panels (kick length panels)
    const isNonManualPanel = Boolean(
        (cabinetGroup.userData as {isNonManualPanel?: boolean}).isNonManualPanel
    );

    cabinetGroup.updateMatrixWorld(true);

    const overlappingCabinets = isNonManualPanel
        ? null
        : checkCabinetOverlap(
              cabinetGroup,
              clampedNewPosition,
              products,
              escapeBuffer
          );

    // Block on the visible body, not dimension arrows. The same escape inset
    // as checkCabinetOverlap lets a near-touch snap instead of rejecting the
    // arrow before Alt snapping runs.
    const overlappingBodies = isNonManualPanel
        ? null
        : checkCabinetBodyOverlap(
              cabinetGroup,
              clampedNewPosition,
              products,
              escapeBuffer
          );

    // Check for collision with other cabinets
    // Skip collision detection for kicker length products when using arrow keys
    if (!isKickerLengthProduct) {
        const hasOverlap = doesMoveOverlap(overlappingBodies, cabinetGroup);

        // If there's an overlap, block the movement and show visual feedback
        if (hasOverlap) {
            triggerOverlapFeedback(cabinetGroup, overlappingBodies);
            return false;
        }
    }

    // Apply snapping to nearby cabinets
    // Enable snapping ONLY when: Alt key is held AND not a repeat event AND not vertical movement
    const enableSnapping =
        event?.altKey && // Alt must be held to enable snapping
        !event?.repeat && // Disable snapping while holding key down
        !isVerticalMovement; // No snapping for vertical movements

    let snapPosition = clampedNewPosition;
    let snapped = false;
    if (enableSnapping) {
        const snapOutcome = handleSnapping(
            cabinetGroup,
            clampedNewPosition,
            products,
            {
                camera,
                overlappingCabinets,
            }
        );

        applySnapState(cabinetGroup, snapOutcome?.snapState);
        if (isSnapCandidate(snapOutcome)) {
            snapPosition = snapOutcome.position;
            snapped = true;
        }
    }

    // Preserve Y position for floor products when snapping (snapping should only affect X and Z)
    const isUpperProduct = cabinetGroup?.userData?.isUpperProduct;
    if (snapped && !isUpperProduct) {
        snapPosition.y = cabinetGroup.position.y;
    }

    // Use snapped position if snapping occurred, otherwise use the clamped new position
    const positionToUse = snapped ? snapPosition : clampedNewPosition;

    // Get wall mesh for boundary checking
    const wallMesh = scene.children.find(
        (ch) =>
            ch.name === ObjectNameMap.rectangularWall ||
            ch.name === ObjectNameMap.lShapeWall
    ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;

    // Check room boundaries using productClamping (for X and Z axes)
    // Pass preventAutoRotation flag to prevent the cabinet from rotating during keyboard movement
    const mouse = new THREE.Vector2(0, 0); // Not used for keyboard movement

    // Use standard room clamping for keyboard moves (same path as base cabinets).
    // Upper products skip wall-pin clamping so arrow keys can move on X and Z.
    // Placeholders can move without a wall mesh (fallback to unclamped position).
    let finalPosition = positionToUse;
    if (wallMesh || !cabinetGroup.userData?.isPlaceholder) {
        const newBasePosition = productClamping(
            cabinetGroup,
            wallMesh,
            camera,
            mouse,
            positionToUse,
            roomType,
            false,
            true // preventAutoRotation - don't rotate cabinet during keyboard movement
        );
        finalPosition = newBasePosition?.clamped || positionToUse;
    }

    // Apply vertical clamping to the final position as well.
    // Arrow keys freeze placeholder Y so productClamping's offset cannot
    // lift/sink them. Page Up/Down keep the Y already clamped above.
    if (cabinetGroup.userData?.isPlaceholder) {
        finalPosition.y = isVerticalMovement
            ? clampedNewPosition.y
            : cabinetGroup.position.y;
    } else {
        finalPosition = applyVerticalClamping(cabinetGroup, finalPosition);
    }

    // Temporarily move to check for overlaps at final position
    cabinetGroup.position.copy(finalPosition);

    // During repeat events (holding key), skip the expensive final overlap
    // check for cabinet-only rooms. Always re-check when a placeholder is
    // involved so keyboard matches drag collision.
    if (
        (!event?.repeat || involvesPlaceholder) &&
        !isKickerLengthProduct &&
        !isNonManualPanel
    ) {
        const overlappingAtFinal = checkCabinetBodyOverlap(
            cabinetGroup,
            cabinetGroup.position,
            products,
            escapeBuffer
        );

        if (doesMoveOverlap(overlappingAtFinal, cabinetGroup)) {
            cabinetGroup.position.copy(previousPosition);
            triggerOverlapFeedback(cabinetGroup, overlappingAtFinal);
            return false;
        }
    }

    // Trigger snap event if snapping occurred
    if (snapped) {
        triggerSnapFeedback(cabinetGroup);
    }

    // Overlay follow uses the same onDrag / transform path as a normal cabinet.
    // Skip userData.move — it toggles Redux `rotating` and re-runs the layout
    // overlay effect every step (that is the lag).
    if (syncUi && wallMesh) {
        handleFillerSwitching(config.products, wallMesh.children);
    }
    updateArrowPositions(cabinetGroup, scene, syncUi);
    if (syncUi) {
        productEvents.revalidateOverlaps();
    }

    return true;
};

/**
 * Updates overlay positions for the cabinet — same path as the layout hook
 * (transform sync + onDrag), not a Redux fingerprint / rotating toggle.
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh
 * @param {THREE.Scene} scene - The scene
 * @param {boolean} rebuildGeometry - Rebuild expensive overlays (distance
 *     lines, dimension geometry). Pose always follows the mesh.
 */
const updateArrowPositions = (
    cabinetGroup: CabinetMesh,
    scene: THREE.Scene,
    rebuildGeometry = true
) => {
    syncOverlaysOnCabinetMove(cabinetGroup, scene, rebuildGeometry);
};

/**
 * Restores the active highlight (blue line) for the selected cabinet
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh
 */
const restoreActiveHighlight = (cabinetGroup: CabinetMesh): void => {
    cabinetGroup.traverse((child) => {
        const userData = child.userData as {
            onCabinetSelect?: (visible: boolean) => void;
        };
        if (typeof userData?.onCabinetSelect === 'function') {
            userData.onCabinetSelect(true);
        }
    });
};

/**
 * Triggers visual feedback when movement is blocked (same path as drag).
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh
 * @param {CabinetMesh[] | null} overlapping - Meshes the mover collided with
 */
const triggerOverlapFeedback = (
    cabinetGroup: CabinetMesh,
    overlapping: CabinetMesh[] | null = null
) => {
    productEvents.triggerEvents(cabinetGroup, 'onOverlap', true);
    overlapping?.forEach((other) => {
        productEvents.triggerEvents(other, 'onOverlap', true);
    });

    // Restore selection on the mover and revalidate so the other item
    // does not stay red after a blocked (or glancing) keyboard step.
    setTimeout(() => {
        productEvents.revalidateOverlaps();
        restoreActiveHighlight(cabinetGroup);
    }, 200);
};

/**
 * Triggers visual feedback when cabinet snaps to another cabinet
 * @param {CabinetMesh} cabinetGroup - The cabinet mesh
 */
const triggerSnapFeedback = (cabinetGroup: CabinetMesh) => {
    // Trigger the onSnap event to show visual feedback (green highlight)
    cabinetGroup.traverse((child) => {
        const userData = child.userData as {
            onSnap?: (snapped: boolean) => void;
        };
        if (typeof userData?.onSnap === 'function') {
            userData.onSnap(true);
        }
    });

    // Restore the blue highlight after showing snap feedback
    setTimeout(() => restoreActiveHighlight(cabinetGroup), 200);
};

/**
 * Rotates an object by the specified degrees around the Y-axis
 * @param {THREE.Object3D} object - The THREE.js object to rotate
 * @param {number} degrees - The rotation amount in degrees
 */
const rotateObject = (object: THREE.Object3D, degrees: number) => {
    const radians = THREE.MathUtils.degToRad(degrees);
    object.rotation.y += radians;
};
